<div class="setup-step">
  @if (pageState() === 'verifying') {
  <div class="review__state d-flex flex-column align-items-center justify-content-center text-center py-5 gap-3">
    <mat-spinner diameter="40"></mat-spinner>
  </div>
  }

  @if (pageState() === 'error') {
  <div class="review__state d-flex flex-column align-items-center justify-content-center text-center py-5 gap-3">
    <mat-icon class="review__state-icon--error icon-lg">error_outline</mat-icon>
    <p class="text-body-secondary mb-0">{{ errorMessage() }}</p>
    <div class="d-flex gap-3">
      <button mat-stroked-button class="px-4 rounded-full" (click)="onBack()">
        <mat-icon>arrow_back</mat-icon>
        {{ 'onboarding.setup.review.back' | translate }}
      </button>
      <button mat-flat-button color="primary" class="px-4 rounded-full" (click)="retry()">
        <mat-icon>refresh</mat-icon>
        {{ 'onboarding.setup.connectIntune.tryAgain' | translate }}
      </button>
    </div>
  </div>
  }

  @if (pageState() === 'ready') { @if (preview(); as p) {
  @let trialEndsDate = (p.trial.trial_ends_at | date: 'longDate');
  @let firstInvoiceDate = (p.trial.first_invoice_at | date: 'longDate');
  @let billingStartsDate = (p.trial.billing_starts_at | date: 'longDate');
  @let stubDays = stubWindowDays(p.trial);
  @let firstInvoiceTotal = money(p.trial.first_invoice_total, p.currency);
  @let monthlyTotal = money(p.monthly_total, p.currency);
  @let dueToday = money(p.due_today, p.currency);
  @let seatUnit = money(p.seat.unit_amount, p.currency);
  @let seatsTotal = money(p.seat.unit_amount * p.seat.quantity, p.currency);
  @let agentUnit = p.agent ? money(p.agent.unit_amount, p.currency) : null;
  @let agentsTotal = p.agent ? money(p.agent.unit_amount * p.agent.quantity, p.currency) : null;

  <div class="text-center setup-step__header">
    <h1 class="mb-1">{{ 'onboarding.setup.review.title' | translate }}</h1>
  </div>

  <div class="review__ready d-flex flex-column align-items-center flex-grow-1">
    <div class="review__card">
      <div class="review__card-body">
        <!-- Promise block -->
        <p class="review__kicker mb-0">{{ 'onboarding.setup.review.promise.kicker' | translate }}</p>

        <p
          class="review__then mb-0"
          [innerHTML]="
            'onboarding.setup.review.promise.freeUntil' | translate: { date: '<b>' + trialEndsDate + '</b>' }
          "
        ></p>

        @if (stubDays > 0) {
        <p
          class="review__then mb-0"
          [innerHTML]="
            'onboarding.setup.review.promise.firstInvoiceProrated'
              | translate
                : {
                    amount: '<b>' + firstInvoiceTotal + '</b>',
                    date: '<b>' + firstInvoiceDate + '</b>',
                    days: '<b>' + stubDays + '</b>'
                  }
          "
        ></p>

        <p
          class="review__then mb-0"
          [innerHTML]="
            'onboarding.setup.review.promise.thenMonthly'
              | translate
                : { amount: '<b>' + monthlyTotal + '</b>', date: '<b>' + billingStartsDate + '</b>' }
          "
        ></p>
        } @else {
        <p
          class="review__then mb-0"
          [innerHTML]="
            'onboarding.setup.review.promise.thenMonthlyNoStub'
              | translate: { amount: '<b>' + monthlyTotal + '</b>' }
          "
        ></p>
        }

        <p
          class="review__pay mb-0"
          [innerHTML]="
            'onboarding.setup.review.promise.payToday' | translate: { amount: '<b>' + dueToday + '</b>' }
          "
        ></p>

        <p class="review__caption mb-0">{{ 'onboarding.setup.review.promise.caption' | translate }}</p>

        <hr class="review__divider" />

        <!-- Breakdown block -->
        <div class="review__breakdown">
          <div class="review__bd-head d-flex justify-content-between align-items-center gap-3 mb-1">
            <span class="review__bd-title">{{ 'onboarding.setup.review.breakdown.title' | translate }}</span>
            <span class="review__bd-plan">
              {{ p.plan.name }}
              <span class="review__offer-badge">{{ offerLabelKey(p.plan.offer_type) | translate }}</span>
            </span>
          </div>

          <div class="review__bd-line d-flex justify-content-between align-items-center gap-3">
            <span>{{ 'onboarding.setup.review.breakdown.seats' | translate }}</span>
            <span class="d-inline-flex align-items-baseline gap-2">
              <span class="review__bd-muted">{{
                'onboarding.setup.review.breakdown.line'
                  | translate: { quantity: p.seat.quantity, amount: seatUnit }
              }}</span>
              <span class="review__bd-amount">{{ seatsTotal }}</span>
            </span>
          </div>

          @if (p.agent; as agent) {
          <div class="review__bd-line d-flex justify-content-between align-items-center gap-3">
            <span>{{ 'onboarding.setup.review.breakdown.agents' | translate }}</span>
            <span class="d-inline-flex align-items-baseline gap-2">
              <span class="review__bd-muted">{{
                'onboarding.setup.review.breakdown.line'
                  | translate: { quantity: agent.quantity, amount: agentUnit }
              }}</span>
              <span class="review__bd-amount">{{ agentsTotal }}</span>
            </span>
          </div>
          }

          <div class="review__bd-sum d-flex justify-content-between align-items-start gap-3">
            <span>{{ 'onboarding.setup.review.breakdown.total' | translate }}</span>
            <span class="d-flex flex-column align-items-end">
              <span class="review__bd-amount">{{ monthlyTotal }}</span>
              <span class="review__bd-varies">{{
                'onboarding.setup.review.breakdown.varies' | translate
              }}</span>
            </span>
          </div>
        </div>
      </div>

      <!-- Footer band -->
      <div class="review__footer">
        @if (isProcessingPayment()) {
        <button mat-flat-button color="primary" disabled class="review__cta w-100 rounded-full">
          <mat-spinner diameter="20" class="d-inline-block me-2"></mat-spinner>
          {{ 'onboarding.setup.review.redirecting' | translate }}
        </button>
        } @else {
        <button mat-flat-button color="primary" class="review__cta w-100 rounded-full" (click)="onAddPaymentMethod()">
          <mat-icon>payment</mat-icon>
          {{ 'onboarding.setup.review.cta' | translate }}
        </button>
        }

        <div class="review__foot-row d-flex justify-content-center align-items-center gap-3 flex-wrap">
          <span class="review__brands d-flex align-items-center gap-2">
            <img
              src="assets/icons/cards/visa.svg"
              [alt]="'onboarding.setup.review.cards.visa' | translate"
              class="review__brand-icon"
            />
            <img
              src="assets/icons/cards/mastercard.svg"
              [alt]="'onboarding.setup.review.cards.mastercard' | translate"
              class="review__brand-icon"
            />
            <img
              src="assets/icons/cards/amex.svg"
              [alt]="'onboarding.setup.review.cards.amex' | translate"
              class="review__brand-icon"
            />
            <img
              src="assets/icons/cards/discover.svg"
              [alt]="'onboarding.setup.review.cards.discover' | translate"
              class="review__brand-icon"
            />
          </span>
          <span class="review__trust d-inline-flex align-items-center gap-1">
            <mat-icon class="review__trust-icon icon-sm">lock</mat-icon>
            {{ 'onboarding.setup.review.trust' | translate }}
          </span>
        </div>
      </div>
    </div>

    @if (errorMessage()) {
    <div class="av-alert av-alert--error d-flex align-items-center gap-2 review__below">
      <mat-icon>error</mat-icon>
      <span class="small">{{ errorMessage() }}</span>
    </div>
    }

    <div class="review__below">
      <button mat-stroked-button class="px-4 rounded-full" (click)="onBack()">
        <mat-icon>arrow_back</mat-icon>
        {{ 'onboarding.setup.review.back' | translate }}
      </button>
    </div>
  </div>
  } }
</div>
